<script setup lang="ts">
/**
 * AsideUserCard — composite shown at the top of AppAside.
 *
 * Figma: inside 2478:133924 (Aside user block).
 * Layout (DOM order, flex-row): [avatar] [name + role] [power button].
 * In RTL the flex auto-flips so the avatar lands on the right.
 *
 * Power button triggers a real logout via authStore — clears the JWT,
 * resets user state, redirects to /login.
 */

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import QbAvatar from '@/components/qb/QbAvatar.vue';

const { t } = useI18n();
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const displayName = computed(() => {
  if (!user.value) return '';
  return user.value.name ?? user.value.email;
});

const subtitle = computed(() => {
  if (!user.value) return '';
  return user.value.role === 'ADMIN'
    ? t('administrator')
    : user.value.email;
});

function onLogout(): void {
  void authStore.logout();
}
</script>

<template>
  <div v-if="user" class="qb-aside-user">
    <QbAvatar :name="displayName" size="md" theme="dark" class="qb-aside-user__avatar" />

    <div class="qb-aside-user__info">
      <p class="qb-aside-user__name" :title="displayName">{{ displayName }}</p>
      <p v-if="subtitle" class="qb-aside-user__role" :title="subtitle">{{ subtitle }}</p>
    </div>

    <button
      type="button"
      class="qb-aside-user__power"
      :aria-label="t('logout')"
      :title="t('logout')"
      @click="onLogout"
    >
      <i class="pi pi-power-off" aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped>
.qb-aside-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: var(--qb-radius-md);
}

.qb-aside-user__avatar {
  flex: 0 0 auto;
}

.qb-aside-user__info {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.qb-aside-user__name {
  margin: 0;
  font-size: 13px;
  font-weight: var(--qb-font-weight-bold);
  color: var(--qb-aside-fg);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.qb-aside-user__role {
  margin: 0;
  font-size: 11px;
  color: var(--qb-aside-fg-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.qb-aside-user__power {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--qb-aside-fg);
  border: none;
  border-radius: var(--qb-radius-sm);
  cursor: pointer;
  transition: background var(--qb-transition-fast);
  font-size: 14px;
}

.qb-aside-user__power:hover {
  background: rgba(255, 255, 255, 0.12);
}

.qb-aside-user__power:active {
  background: rgba(255, 255, 255, 0.18);
}
</style>
